
import React, { useEffect, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { 
  Form, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormControl, 
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Factory } from "@/hooks/useFactories";
import { FactoryCheckboxSelect } from "./FactoryCheckboxSelect";
import { Checkbox } from "@/components/ui/checkbox";
import { Package } from "lucide-react";
import { ImageUpload } from "./ImageUpload";

export const categorySchema = z.object({
  name: z.string().min(1, "Product name is required"),
  description: z.string().optional(),
  product_prefix: z.string().max(12, "Product prefix cannot exceed 12 characters").optional(),
  image_url: z.string().optional(),
  factories: z.array(z.string()).default([]),
  is_bundle: z.boolean().default(true),
  bundle_products: z.array(z.string()).default([]),
  sort_order: z.number().min(1, "Sort order must be at least 1").default(999),
});

export type CategoryFormValues = z.infer<typeof categorySchema>;

interface CategoryFormProps {
  onSubmit: (values: CategoryFormValues) => Promise<void>;
  defaultValues: CategoryFormValues;
  onCancel: () => void;
  isEditing: boolean;
  factories: Factory[];
  isLoadingFactories: boolean;
  allProducts?: any[];
}

export function CategoryForm({ 
  onSubmit, 
  defaultValues, 
  onCancel, 
  isEditing,
  factories,
  isLoadingFactories,
  allProducts = []
}: CategoryFormProps) {
  const [nonBundleProducts, setNonBundleProducts] = useState<any[]>([]);
  const [showBundleProducts, setShowBundleProducts] = useState(defaultValues.is_bundle);
  
  const currentProductId = isEditing && allProducts.length > 0 
    ? allProducts.find(p => 
        p.name === defaultValues.name && 
        p.description === defaultValues.description
      )?.id
    : undefined;

  const form = useForm<CategoryFormValues>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      ...defaultValues,
      factories: defaultValues.factories || [],
      is_bundle: defaultValues.is_bundle !== undefined ? defaultValues.is_bundle : true,
      bundle_products: defaultValues.bundle_products || [],
      sort_order: defaultValues.sort_order || 999,
    }
  });

  useEffect(() => {
    if (allProducts.length > 0) {
      const filtered = allProducts.filter(
        product => !product.is_bundle && (!currentProductId || product.id !== currentProductId)
      );
      setNonBundleProducts(filtered);
    }
  }, [allProducts, currentProductId]);

  const isBundle = form.watch("is_bundle");
  useEffect(() => {
    setShowBundleProducts(isBundle);
  }, [isBundle]);

  const selectedFactoryNames = form.watch("factories") || [];
  const selectedBundleProducts = form.watch("bundle_products") || [];

  const handleFactoriesChange = (factoryNames: string[]) => {
    form.setValue("factories", factoryNames, { 
      shouldValidate: true,
      shouldDirty: true
    });
  };

  const handleBundleProductsChange = (productId: string) => {
    const currentProducts = [...selectedBundleProducts];
    
    if (currentProducts.includes(productId)) {
      form.setValue("bundle_products", currentProducts.filter(id => id !== productId), {
        shouldValidate: true,
        shouldDirty: true
      });
    } else {
      form.setValue("bundle_products", [...currentProducts, productId], {
        shouldValidate: true,
        shouldDirty: true
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Product Name</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Enter product name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Product description" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="product_prefix"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Product Prefix</FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  placeholder="Enter product prefix (max 12 characters)" 
                  maxLength={12}
                />
              </FormControl>
              <FormMessage />
              <p className="text-sm text-muted-foreground">
                Maximum 12 characters for product identification
              </p>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="sort_order"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Priority (Sort Order)</FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  type="number" 
                  min="1"
                  placeholder="Enter priority (lower number = higher priority)" 
                  onChange={(e) => field.onChange(parseInt(e.target.value) || 999)}
                />
              </FormControl>
              <FormMessage />
              <p className="text-sm text-muted-foreground">
                Lower numbers appear first (e.g., 1 will show before 2)
              </p>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="image_url"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <ImageUpload
                  value={field.value || ""}
                  onChange={field.onChange}
                  label="Product Image"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="is_bundle"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Is bundle</FormLabel>
                <p className="text-sm text-muted-foreground">
                  Check this if the product is a bundle
                </p>
              </div>
            </FormItem>
          )}
        />

        {showBundleProducts && nonBundleProducts.length > 0 && (
          <div className="rounded-md border p-4 space-y-2">
            <FormLabel>Select products for this bundle:</FormLabel>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {nonBundleProducts.map(product => (
                <div key={product.id} className="flex items-center space-x-2">
                  <Checkbox 
                    id={`product-${product.id}`}
                    checked={selectedBundleProducts.includes(product.id)}
                    onCheckedChange={() => handleBundleProductsChange(product.id)}
                  />
                  <label 
                    htmlFor={`product-${product.id}`}
                    className="text-sm cursor-pointer flex items-center gap-1"
                  >
                    <Package className="h-4 w-4 text-muted-foreground" />
                    {product.name}
                  </label>
                </div>
              ))}
            </div>
            {nonBundleProducts.length === 0 && (
              <p className="text-sm text-muted-foreground">No products available to select</p>
            )}
          </div>
        )}

        <FormField
          control={form.control}
          name="factories"
          render={() => (
            <FormItem>
              <FormLabel>Associated Factories</FormLabel>
              <FormControl>
                <FactoryCheckboxSelect
                  factories={factories}
                  selectedFactoryNames={selectedFactoryNames}
                  onChange={handleFactoriesChange}
                  isLoading={isLoadingFactories}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit">
            {isEditing ? "Update Product" : "Add Product"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
